// BridgeScreen: the branded full-screen loading/splash overlay shown at boot.
// Renders a brand mark (or an icon-fill progress visual), title, optional
// tagline, optional progress bar / percent text, and — when sign-in is required
// — a "Sign in with Google" button. Everything is configurable so a game can go
// from a rich splash down to just brandName + icon.
//
// Progress is monotonic: reportLoadProgress() can only move the visual forward,
// and there is no implicit/fallback progress — it starts at exactly 0.
//
// No-ops gracefully when there's no DOM (Node/tests), so the SDK runs anywhere.

import { hasDOM } from '../platform/env.js';

// Branded stacked wordmark for "The Butterfly Universe": THE (small) / BUTTERFLY
// (large navy) / UNIVERSE (medium warm accent), with an optional descriptor.
// Wide uppercase geometric feel; responsive via clamp(). Uses the configured
// textColor (navy) and accentFrom (warm orange) so themes still apply.
function brandedWordmark(c) {
  const wrap = document.createElement('div');
  wrap.style.cssText = 'display:flex;flex-direction:column;align-items:center;text-align:center;line-height:1;gap:7px;font-family:system-ui,-apple-system,Segoe UI,Roboto,sans-serif';

  // padding-left offsets the trailing letter-spacing so each line stays optically centered.
  const mk = (text, css) => { const el = document.createElement('div'); el.textContent = text; el.style.cssText = css; return el; };
  const span = (text, color) => { const s = document.createElement('span'); s.textContent = text; if (color) s.style.color = color; return s; };

  // THE
  wrap.append(mk('THE',
    `color:${c.textColor};text-transform:uppercase;font-weight:600;font-size:clamp(10px,2.6vw,14px);letter-spacing:0.55em;padding-left:0.55em`));

  // BUTTERFLY — navy with a warm-accent "R" (matches the logo).
  const big = document.createElement('div');
  big.style.cssText = `color:${c.textColor};text-transform:uppercase;font-weight:800;font-size:clamp(30px,9vw,58px);letter-spacing:0.14em;padding-left:0.14em`;
  big.append(span('BUTTE'), span('R', c.accentFrom), span('FLY'));
  wrap.append(big);

  // — UNIVERSE — (warm-accent text flanked by dashes)
  const uniRow = document.createElement('div');
  uniRow.style.cssText = 'display:flex;align-items:center;justify-content:center;gap:clamp(8px,3vw,16px)';
  const dash = () => mk('', `height:2px;width:clamp(14px,5vw,34px);background:${c.accentFrom};border-radius:2px`);
  const uni = mk('UNIVERSE',
    `color:${c.accentFrom};text-transform:uppercase;font-weight:700;font-size:clamp(15px,4.4vw,26px);letter-spacing:0.34em;padding-left:0.34em`);
  uniRow.append(dash(), uni, dash());
  wrap.append(uniRow);

  // MEDIA • ENTERTAINMENT (optional descriptor)
  if (c.brandSubtitle) {
    wrap.append(mk(String(c.brandSubtitle).toUpperCase(),
      `color:${c.textColor};opacity:.8;text-transform:uppercase;font-weight:600;font-size:clamp(8px,2.2vw,11px);letter-spacing:0.32em;padding-left:0.32em;margin-top:2px`));
  }

  // -*- sparkle divider (the logo's flourish): a line broken by a 4-point star.
  const divider = document.createElement('div');
  divider.style.cssText = 'margin-top:6px;line-height:0';
  divider.innerHTML =
    `<svg viewBox="0 0 200 16" style="width:clamp(150px,55vw,230px);height:14px;display:block" fill="none" xmlns="http://www.w3.org/2000/svg">` +
    `<line x1="2" y1="8" x2="86" y2="8" stroke="${c.accentFrom}" stroke-width="1.6" stroke-linecap="round"/>` +
    `<line x1="114" y1="8" x2="198" y2="8" stroke="${c.accentFrom}" stroke-width="1.6" stroke-linecap="round"/>` +
    `<path d="M100 1 L102.2 5.8 L107 8 L102.2 10.2 L100 15 L97.8 10.2 L93 8 L97.8 5.8 Z" fill="${c.accentFrom}"/>` +
    `</svg>`;
  wrap.append(divider);

  return wrap;
}

export class BridgeScreen {
  constructor(cfg, ctx) {
    this.cfg = cfg;
    this.log = ctx?.log;
    this.root = null;
    this.bar = null;          // horizontal progress fill (bar mode)
    this.track = null;        // horizontal progress track
    this.iconFillEl = null;   // clipped "filled" icon (icon-fill mode)
    this.progressTextEl = null;
    this.signInWrap = null;
    this.statusEl = null;
    this._shownAt = 0;
    this._progress = 0;       // starts at 0; never decreases
    this._dismissing = null;  // in-flight/settled dismiss promise (idempotency)
  }

  show() {
    if (!hasDOM()) { this.log?.info?.('BridgeScreen: no DOM, skipping splash.'); return; }
    if (this.root) return;
    const c = this.cfg;
    const grad = `linear-gradient(90deg, ${c.accentFrom}, ${c.accentMid}, ${c.accentTo})`;

    const el = document.createElement('div');
    el.setAttribute('data-bf-bridge', '');
    el.style.cssText = [
      'position:fixed', 'inset:0', 'z-index:2147483647', 'display:flex',
      'flex-direction:column', 'align-items:center', 'justify-content:center',
      'gap:16px', `background:${c.background}`, `color:${c.textColor}`,
      'font-family:system-ui,-apple-system,Segoe UI,Roboto,sans-serif',
      'opacity:1', 'transition:opacity .4s ease',
    ].join(';');
    // Optional full-bleed brand artwork over the base color.
    if (c.backgroundImage) {
      el.style.backgroundImage = `url("${c.backgroundImage}")`;
      el.style.backgroundSize = 'cover';
      el.style.backgroundPosition = 'center';
      el.style.backgroundRepeat = 'no-repeat';
    }

    // ---- Visual: icon-fill stack, or the brand mark ----------------------
    const useIconFill = c.progressMode === 'icon-fill' && c.iconUnfilledUrl && c.iconFilledUrl;
    if (c.progressMode === 'icon-fill' && !useIconFill) {
      this.log?.warn?.('BridgeScreen: progressMode "icon-fill" needs iconUnfilledUrl + iconFilledUrl; falling back to the brand mark.');
    }

    if (useIconFill) {
      const wrap = document.createElement('div');
      // Negative margin reclaims the square icon's built-in bottom whitespace so
      // the wordmark sits ~24px under the butterfly (compact, like the logo).
      wrap.style.cssText = 'position:relative;display:inline-block;line-height:0;margin-bottom:clamp(-72px,-11vw,-44px)';

      const base = document.createElement('img');
      base.src = c.iconUnfilledUrl;
      base.alt = c.brandName || '';
      base.draggable = false;
      // Larger, responsive icon so it's the clear focal point of the splash.
      base.style.cssText = 'display:block;width:auto;height:auto;max-width:clamp(200px,48vw,300px);max-height:clamp(200px,48vw,300px);user-select:none';

      const fill = document.createElement('img');
      fill.src = c.iconFilledUrl;
      fill.alt = '';
      fill.draggable = false;
      // Clip the whole image away initially (inset from the top = 100%), so 0%
      // progress shows none of the fill. setProgress() raises it from the bottom.
      fill.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;object-fit:contain;clip-path:inset(100% 0 0 0);-webkit-clip-path:inset(100% 0 0 0);transition:clip-path .2s ease,-webkit-clip-path .2s ease;user-select:none';

      wrap.append(base, fill);
      el.appendChild(wrap);
      this.iconFillEl = fill;
    } else {
      const mark = document.createElement('div');
      if (c.logoUrl) {
        mark.innerHTML = `<img src="${c.logoUrl}" alt="${c.brandName || ''}" style="max-width:160px;max-height:160px" />`;
      } else {
        mark.style.cssText = `width:92px;height:92px;border-radius:50%;background:${grad};box-shadow:0 0 48px ${c.accentMid}66`;
      }
      el.appendChild(mark);
    }

    // ---- Title -----------------------------------------------------------
    if (c.brandName) {
      const isUniverse = String(c.brandName).trim().toLowerCase() === 'the butterfly universe';
      if (isUniverse) {
        // Branded stacked wordmark matching the logo.
        el.appendChild(brandedWordmark(c));
      } else {
        // Any other game name renders as a normal single-line title.
        const title = document.createElement('div');
        title.textContent = c.brandName;
        title.style.cssText = `font-size:clamp(20px,6vw,28px);font-weight:700;letter-spacing:1px;color:${c.textColor};text-align:center`;
        el.appendChild(title);
      }
    }

    // ---- Tagline ---------------------------------------------------------
    if (c.showTagline && c.tagline) {
      const tag = document.createElement('div');
      tag.textContent = c.tagline;
      tag.style.cssText = 'font-size:14px;opacity:.7';
      el.appendChild(tag);
    }

    // ---- Progress bar (gated on showProgressBar in either mode) ----------
    if (c.showProgressBar) {
      const track = document.createElement('div');
      track.style.cssText = 'width:240px;height:6px;border-radius:99px;background:#ffffff22;overflow:hidden';
      const bar = document.createElement('div');
      bar.style.cssText = `height:100%;width:0%;border-radius:99px;background:${grad};transition:width .25s ease`;
      track.appendChild(bar);
      el.appendChild(track);
      this.track = track;
      this.bar = bar;
    }

    // ---- Progress percent text -------------------------------------------
    if (c.showProgressText) {
      const pt = document.createElement('div');
      pt.style.cssText = 'font-size:13px;opacity:.75;font-variant-numeric:tabular-nums';
      el.appendChild(pt);
      this.progressTextEl = pt;
    }

    // ---- Sign-in slot + status ------------------------------------------
    const signInWrap = document.createElement('div');
    signInWrap.style.cssText = 'display:none;flex-direction:column;align-items:center;gap:10px';

    const status = document.createElement('div');
    status.style.cssText = 'font-size:12px;opacity:.6';

    el.append(signInWrap, status);
    document.body.appendChild(el);

    this.root = el;
    this.signInWrap = signInWrap;
    this.statusEl = status;
    this._shownAt = Date.now();
    this.setProgress(this._progress); // paint initial (0) state
    this.log?.debug?.(`[bridge] shown (progressMode=${c.progressMode})`);
  }

  // Move progress forward only. value is 0..1.
  setProgress(value) {
    const v = Math.max(0, Math.min(1, Number(value) || 0));
    const next = Math.max(this._progress, v); // monotonic: never goes backward
    if (next === this._progress && this.root) {
      // Ignored (would move backward or no change), but still log what we got.
      this.log?.debug?.(`[bridge] progress ignored (got ${v}, holding ${this._progress})`);
      return;
    }
    this._progress = next;
    const pct = this._progress * 100;
    if (this.bar) this.bar.style.width = `${pct}%`;
    if (this.iconFillEl) {
      const clip = `inset(${100 - pct}% 0 0 0)`;
      this.iconFillEl.style.clipPath = clip;
      this.iconFillEl.style.webkitClipPath = clip;
    }
    if (this.progressTextEl) this.progressTextEl.textContent = `${Math.round(pct)}%`;
    this.log?.debug?.(`[bridge] progress applied ${pct.toFixed(1)}%`);
  }

  setStatus(text) { if (this.statusEl) this.statusEl.textContent = text || ''; }

  // Show a "Sign in with Google" button. onSignIn fires on click (a user gesture,
  // required for the OAuth redirect). The progress bar is hidden until signed in.
  showSignIn({ onSignIn } = {}) {
    if (!this.signInWrap) return;
    this.signInWrap.innerHTML = '';
    this.signInWrap.style.display = 'flex';
    if (this.track) this.track.style.display = 'none';

    const G_ICON = "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 48 48'><path fill='%23EA4335' d='M24 9.5c3.54 0 6.7 1.22 9.2 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z'/><path fill='%234285F4' d='M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z'/><path fill='%23FBBC05' d='M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z'/><path fill='%2334A853' d='M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z'/></svg>";

    const btn = document.createElement('button');
    btn.type = 'button';
    btn.style.cssText = 'display:inline-flex;align-items:center;gap:10px;padding:11px 18px;border:0;border-radius:10px;background:#fff;color:#1f1f1f;font-size:14px;font-weight:600;cursor:pointer;box-shadow:0 2px 14px #00000040';
    btn.innerHTML = `<span style="width:18px;height:18px;background:url('${G_ICON}') center/contain no-repeat"></span>Sign in with Google`;
    btn.onclick = () => {
      btn.disabled = true;
      btn.style.opacity = '.6';
      btn.style.cursor = 'default';
      this.setStatus('Redirecting to Google…');
      onSignIn?.();
    };

    const note = document.createElement('div');
    note.textContent = 'Sign in to save progress and join the leaderboards.';
    note.style.cssText = 'font-size:12px;opacity:.65';

    this.signInWrap.append(btn, note);
  }

  // Called once the player is signed in: hide the button, restore the progress UI.
  setSignedIn(player) {
    if (this.signInWrap) { this.signInWrap.style.display = 'none'; this.signInWrap.innerHTML = ''; }
    if (this.track) this.track.style.display = '';
    this.setStatus(player?.displayName ? `Signed in as ${player.displayName}` : 'Signed in');
  }

  // Dismiss only happens here (from sdk.start()), and respects minDurationMs.
  // Idempotent: concurrent/repeat calls share one run; once removed it resolves
  // safely.
  dismiss() {
    if (this._dismissing) return this._dismissing;
    this._dismissing = this._runDismiss();
    return this._dismissing;
  }

  async _runDismiss() {
    this.log?.debug?.('[bridge] dismiss started');
    // Snap the indicator to 100% so the icon/bar is visibly full before fading.
    this.setProgress(1);
    if (this.root) {
      const wait = Math.max(0, (this.cfg.minDurationMs || 0) - (Date.now() - this._shownAt));
      if (wait) await new Promise((r) => setTimeout(r, wait));
      this.root.style.opacity = '0';
      await new Promise((r) => setTimeout(r, 400));
      this.root.remove();
      this.root = null;
    }
    this.log?.debug?.('[bridge] dismiss completed');
  }
}
